/**
 * Auszahlungen — the monthly payouts CoLiviq has transferred to this owner,
 * and the statement behind each one (COL-528).
 *
 * The page answers one question: what should I have received? So it shows a
 * month ONLY once the money has actually gone out. Anything pending, ready to
 * pay or outstanding is absent — an owner seeing a payout before the transfer
 * leaves would chase CoLiviq for money still in flight. That filtering is the
 * RPC's, not this component's.
 *
 * NOTHING HERE IS RECOMPUTED. The prototype worked the fee out as 15% of the
 * rent in the browser; every figure on this page is read from the row the
 * payout confirmation recorded, so the dialog, the list and the PDF cannot
 * disagree about how much money changed hands.
 *
 * The page title is "Auszahlungen", matching the nav. The prototype still said
 * "Rechnungen / Invoices", which is the thing the owner is NOT looking at: an
 * invoice is what CoLiviq charges, a payout is what the owner gets.
 */
import { useState } from "react";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { formatEuroCents } from "@/lib/owner-money";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useOwnerPayoutStatements,
  type OwnerPayoutStatement,
} from "@/hooks/owner/useOwnerPayoutStatements";
import { OwnerPayoutStatementDialog } from "@/components/owner/OwnerPayoutStatementDialog";
import { useDownloadPayoutStatement } from "@/hooks/owner/useDownloadPayoutStatement";

const C = {
  petrol: "#0F4C4A",
  hairline: "#ECE7DD",
  muted: "#7A8E8C",
  body: "#47605E",
  sand: "#FBF9F5",
} as const;

export default function OwnerPayoutsPage() {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const { data: rows = [], isLoading, isError } = useOwnerPayoutStatements();
  const [open, setOpen] = useState<OwnerPayoutStatement | null>(null);
  // Same mutation the dialog uses: one place that knows how a statement is
  // fetched and saved, whichever button starts it.
  const download = useDownloadPayoutStatement();

  const monthLabel = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(locale, {
      month: "long",
      year: "numeric",
    });

  return (
    <AdminV2Layout title={t("owner.payouts.title", "Auszahlungen")}>
      <p className="mb-6 text-[13px]" style={{ color: C.muted }}>
        {t(
          "owner.payouts.intro",
          "Ihre monatlichen Gutschriften: eingezogene Miete, coliviq-Gebühr und der an Sie überwiesene Nettobetrag."
        )}
      </p>

      {isLoading && (
        <p className="py-9 text-center text-[13px]" style={{ color: C.muted }}>
          {t("owner.payouts.loading", "Wird geladen…")}
        </p>
      )}

      {/* "No payouts yet" and "we could not load your payouts" are different
          facts about an owner's money, and only one of them is reassuring. */}
      {isError && (
        <p
          className="py-9 text-center text-[13px]"
          style={{ color: "#C0714F" }}
          role="alert"
        >
          {t(
            "owner.payouts.loadFailed",
            "Ihre Auszahlungen konnten nicht geladen werden. Bitte laden Sie die Seite neu."
          )}
        </p>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-[15px] font-semibold" style={{ color: C.petrol }}>
            {t("owner.payouts.emptyTitle", "Noch keine Auszahlungen")}
          </p>
          <p
            className="mx-auto mt-2 max-w-sm text-[13px]"
            style={{ color: C.muted }}
          >
            {t(
              "owner.payouts.emptyBody",
              "Sobald coliviq die erste Miete an Sie überwiesen hat, finden Sie hier die Gutschrift dazu."
            )}
          </p>
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        // No horizontal scroll at desktop width: the prototype clipped its
        // Download column at the right edge. Month and property take what they
        // need, the figure and the actions are sized to their content.
        <table className="w-full border-collapse text-[13.5px]">
          <thead>
            <tr className="text-xs" style={{ color: C.muted }}>
              <th
                className="border-b pb-2.5 text-left font-normal"
                style={{ borderColor: C.hairline }}
              >
                {t("owner.payouts.colMonth", "Monat")}
              </th>
              <th
                className="border-b pb-2.5 text-left font-normal"
                style={{ borderColor: C.hairline }}
              >
                {t("owner.payouts.colProperty", "Objekt")}
              </th>
              <th
                className="border-b pb-2.5 text-right font-normal"
                style={{ borderColor: C.hairline }}
              >
                {t("owner.payouts.colNet", "Netto-Auszahlung")}
              </th>
              <th
                className="border-b pb-2.5"
                style={{ borderColor: C.hairline }}
              >
                <span className="sr-only">
                  {t("owner.payouts.colActions", "Aktionen")}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.invoice_id}
                className="border-b transition-colors hover:bg-[#FBF9F5]"
                style={{ borderColor: C.hairline }}
              >
                <td
                  className="whitespace-nowrap py-3 font-medium"
                  style={{ color: C.petrol }}
                >
                  {monthLabel(r.period_month)}
                </td>
                <td className="py-3" style={{ color: C.body }}>
                  {r.property_title ?? "—"}
                </td>
                <td
                  className="py-3 text-right font-semibold tabular-nums"
                  style={{ color: C.petrol }}
                >
                  {formatEuroCents(r.net_payout_cents, locale)}
                </td>
                <td className="whitespace-nowrap py-3 text-right">
                  <span className="inline-flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setOpen(r)}
                      className="h-7 rounded-full border px-3 text-xs font-medium transition-colors hover:bg-[#F7F5F0]"
                      style={{ borderColor: C.hairline, color: C.petrol }}
                    >
                      {t("owner.payouts.view", "Ansehen")}
                    </button>
                    <button
                      type="button"
                      onClick={() => download.mutate(r.invoice_id)}
                      disabled={download.isPending}
                      className="h-7 rounded-full border px-3 text-xs font-medium transition-colors hover:bg-[#F7F5F0] disabled:opacity-60"
                      style={{ borderColor: C.hairline, color: C.petrol }}
                    >
                      {t("owner.payouts.download", "Herunterladen")}
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <OwnerPayoutStatementDialog
        statement={open}
        onOpenChange={(next) => !next && setOpen(null)}
      />
    </AdminV2Layout>
  );
}
